import React, { useState } from 'react'
import { Box, Container, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makePostRequest } from 'utils/api'

/**
 * Life Intelligence — chat IA GÉNÉRALISTE multi-domaines.
 *
 * Pattern : strict miroir de `ConseillerTempsPage`, mais sans panneaux
 * d'analyse : la page est un chat plein écran avec le Life Agent, capable de
 * répondre sur TOUS les domaines de l'app (finance, temps, tâches, objectifs,
 * carrière, foyer, social, santé, sport…). Le contexte multi-domaines est
 * construit CÔTÉ SERVEUR (snapshot MCP `get_user_context`), donc aucun agrégat
 * n'est calculé ici.
 *
 * Accès : bouton « Life Intelligence » du header de la page d'accueil
 * (HomeToggleHeader). Route : /life-intelligence (cf. App.js).
 *
 * Coût en crédits : 1 crédit / message chat (feature `life_intelligence`).
 * HTTP 402 → modale « Crédits insuffisants » globale (utils/api.js).
 * Si le backend `/api/general/life-intelligence/chat/` n'est pas exposé,
 * on retombe sur le fallback démo de ConseillerChat.
 */
function LifeIntelligencePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')
  const siteUser = useSelector((s) => s.user)

  // Amorçage optionnel (même trame que le Conseiller Temps) : une carte
  // SmartFeed pourra pointer ici avec un prompt via le state de navigation.
  const [autoSendPrompt] = useState(() =>
    (location.state?.seedPrompt || '').trim(),
  )

  const displayedFirstName =
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  /**
   * Envoi d'un message au « Life Agent — Life Intelligence ».
   * CONTRAT BACKEND :
   *   POST /api/general/life-intelligence/chat/
   *     body: { message, locale, history, feature: 'life_intelligence' }
   *     output: { reply, source, locale, generated_at }
   *     coût : 1 crédit, feature `life_intelligence`
   *     erreurs HTTP 402 → modale "Crédits insuffisants" globale (utils/api.js)
   */
  const handleChatSend = async (message, locale, history = []) => {
    try {
      const response = await makePostRequest(
        '/api/general/life-intelligence/chat/',
        { message, locale, history, feature: 'life_intelligence' },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[LifeIntelligence] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('lifeIntelligence.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 👋 Je suis Life Intelligence, votre assistant IA connecté à toute l'app : budget, patrimoine, temps, tâches, objectifs, carrière, foyer, social, santé, sport… Posez-moi une question, ou donnez-moi un ordre (créer une tâche, une note, un projet…) : je vous récapitule l'action et je l'exécute dès votre confirmation.`,
        name: displayedFirstName,
      }),
      source: t('lifeIntelligence.signature', {
        defaultValue: 'Life Agent — Life Intelligence',
      }),
      timestamp: new Date().toISOString(),
    },
  ]

  const suggestedPrompts = [
    t('lifeIntelligence.prompts.overview', {
      defaultValue: 'Fais-moi un état des lieux global de ma situation.',
    }),
    t('lifeIntelligence.prompts.priorities', {
      defaultValue: 'Quelles devraient être mes 3 priorités cette semaine ?',
    }),
    t('lifeIntelligence.prompts.budget', {
      defaultValue: 'Comment se porte mon budget ce mois-ci ?',
    }),
    t('lifeIntelligence.prompts.goals', {
      defaultValue: 'Où en suis-je par rapport à mes objectifs de vie ?',
    }),
    t('lifeIntelligence.prompts.balance', {
      defaultValue: 'Mon équilibre de vie est-il cohérent avec mes priorités ?',
    }),
    t('lifeIntelligence.prompts.action', {
      defaultValue: 'Crée-moi une tâche pour demain : …',
    }),
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar — même trame que les conseillers. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(0,0,29,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/')}
            sx={{ color: '#dbe5f7', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <AutoAwesomeIcon sx={{ color: '#7e9cdb', flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('lifeIntelligence.pageTitle', {
                defaultValue: 'Life Intelligence',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#a0b4d6 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('lifeIntelligence.pageSubtitle', {
                defaultValue: 'Votre assistant IA connecté à tous vos domaines de vie',
              })}
            </WTypography>
          </Box>
        </Container>
      </Box>

      {/* Chat plein écran, centré. */}
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 1.5, md: 3 },
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ConseillerChat
            messages={initialChatMessages}
            suggestedPrompts={suggestedPrompts}
            userLocale={i18n.language || 'fr-FR'}
            agentSignature={t('lifeIntelligence.signature', {
              defaultValue: 'Life Agent — Life Intelligence',
            })}
            onSend={handleChatSend}
            autoSendPrompt={autoSendPrompt}
          />
        </Box>
      </Container>
    </Box>
  )
}

export default LifeIntelligencePage
